// Log a call, WhatsApp, email or meeting on a lead or a contact, and how a
// logged one reads in an activity timeline.
//
//   lead .... POST /api/leads/:id/activities     (leadController.addActivity)
//   contact . POST /api/contacts/:id/activities  (contactController.addActivity)
// Both take { activity_type, title, description, call_outcome,
// call_duration_seconds, meeting_datetime, meeting_location }; only the lead
// one stores meeting_outcome, so on a contact the outcome goes into the note.
// call_outcome is the table's ENUM (connected, no_answer, busy, voicemail,
// wrong_number, callback_requested).
import { useEffect, useState } from "react";
import { Drawer, DrawerField, DrawerGroup, ChoiceRow, drawerInput } from "../Drawer";
import { PrimaryButton } from "../ui";
import { TimeInput } from "./leadsKit";

export const LOG_TYPES = ["Call", "WhatsApp", "Email", "Meeting"];
const TYPE_KEY = { Call: "call", WhatsApp: "whatsapp", Email: "email", Meeting: "meeting" };
export const CALL_OUTCOMES = [
  ["connected", "Connected"], ["no_answer", "No answer"], ["busy", "Busy"],
  ["voicemail", "Voicemail"], ["wrong_number", "Wrong number"], ["callback_requested", "Asked to call back"],
];
const OUTCOME_LABEL = Object.fromEntries(CALL_OUTCOMES);

const pad = (n) => String(n).padStart(2, "0");
const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const nowHm = () => { const d = new Date(); return `${pad(d.getHours())}:${pad(d.getMinutes() - (d.getMinutes() % 15))}`; };

/** "3 min 20 s", "45 s". */
export function fmtDuration(seconds) {
  const s = Number(seconds) || 0;
  if (!s) return "";
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m ? `${m} min${r ? ` ${r} s` : ""}` : `${r} s`;
}

/**
 * A logged call / message / meeting row → { text, detail } for a timeline.
 * Returns null for any other activity type, so callers keep their own wording.
 */
export function describeLogged(a) {
  const type = a.activity_type;
  if (!["call", "whatsapp", "email", "sms", "meeting"].includes(type)) return null;
  const lines = [];
  if (type === "call") {
    const bits = [OUTCOME_LABEL[a.call_outcome], fmtDuration(a.call_duration_seconds)].filter(Boolean);
    if (a.description) lines.push(a.description);
    return { text: bits.length ? `Call · ${bits.join(" · ")}` : a.title || "Logged a call", detail: lines.join("\n") };
  }
  if (type === "meeting") {
    const when = a.meeting_datetime ? new Date(a.meeting_datetime) : null;
    const whenText = when && !Number.isNaN(when.getTime()) ? when.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "";
    if (a.meeting_outcome) lines.push(`Outcome: ${a.meeting_outcome}`);
    if (a.description) lines.push(a.description);
    return { text: ["Meeting", whenText, a.meeting_location].filter(Boolean).join(" · "), detail: lines.join("\n") };
  }
  const label = type === "whatsapp" ? "WhatsApp" : type === "email" ? "Email" : "SMS";
  return { text: a.title || `${label} logged`, detail: a.description || "" };
}

/**
 * Drawer to log one activity. `target`: "lead" | "contact" (a contact can't
 * store a meeting outcome on its own field). `onSave(body)` must reject on
 * failure; its message is shown and the drawer stays open.
 */
export function LogActivityDrawer({ open, onClose, onSave, target = "lead", context, initialType = "Call" }) {
  const [type, setType] = useState(initialType);
  const [outcome, setOutcome] = useState("connected");
  const [minutes, setMinutes] = useState("");
  const [meetDate, setMeetDate] = useState(todayIso());
  const [meetTime, setMeetTime] = useState(nowHm());
  const [location, setLocation] = useState("");
  const [meetOutcome, setMeetOutcome] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setType(initialType); setOutcome("connected"); setMinutes(""); setMeetDate(todayIso()); setMeetTime(nowHm());
    setLocation(""); setMeetOutcome(""); setNotes(""); setSaving(false); setError("");
  }, [open, initialType]);

  const minutesValid = minutes === "" || (/^\d+(\.\d+)?$/.test(minutes) && Number(minutes) <= 600);
  const needsNote = (type === "WhatsApp" || type === "Email") && !notes.trim();
  const valid = minutesValid && !needsNote && (type !== "Meeting" || (meetDate && meetTime));
  const dirty = !!(notes.trim() || minutes || location || meetOutcome);

  const save = async () => {
    if (!valid || saving) return;
    const key = TYPE_KEY[type];
    const body = { activity_type: key };
    if (type === "Call") {
      body.call_outcome = outcome;
      if (minutes !== "") body.call_duration_seconds = Math.round(Number(minutes) * 60);
      body.title = `Call · ${OUTCOME_LABEL[outcome]}`;
      if (notes.trim()) body.description = notes.trim();
    } else if (type === "Meeting") {
      body.title = "Meeting";
      body.meeting_datetime = `${meetDate} ${meetTime}:00`;
      if (location.trim()) body.meeting_location = location.trim();
      if (target === "lead") {
        if (meetOutcome.trim()) body.meeting_outcome = meetOutcome.trim();
        if (notes.trim()) body.description = notes.trim();
      } else {
        const text = [meetOutcome.trim() ? `Outcome: ${meetOutcome.trim()}` : "", notes.trim()].filter(Boolean).join("\n");
        if (text) body.description = text;
      }
    } else {
      body.title = type === "WhatsApp" ? "WhatsApp message" : "Email";
      body.description = notes.trim();
    }
    setSaving(true);
    setError("");
    try {
      await onSave(body);
      onClose();
    } catch (e) {
      setSaving(false);
      setError(e?.response?.data?.message || e?.message || "Couldn't log this. Try again.");
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Log activity"
      context={context}
      dirty={dirty && !saving}
      labelledBy="log-activity-title"
      actions={<PrimaryButton type="button" onClick={save} disabled={!valid} loading={saving} loadingLabel="Saving…" className="h-11 px-5 text-small">Log {type.toLowerCase()}</PrimaryButton>}
    >
      <div className="flex flex-col">
        <DrawerGroup>
          <DrawerField label="What happened">
            <ChoiceRow label="Activity type" value={type} options={LOG_TYPES} onChange={(t) => { setType(t); setError(""); }} />
          </DrawerField>
        </DrawerGroup>
        {type === "Call" && (
          <DrawerGroup title="Call">
            <DrawerField label="Outcome">
              <div role="radiogroup" aria-label="Call outcome" className="flex flex-wrap gap-1.5">
                {CALL_OUTCOMES.map(([value, label]) => (
                  <button key={value} type="button" role="radio" aria-checked={outcome === value} onClick={() => setOutcome(value)} className={`flex h-9 items-center rounded-full px-3.5 text-small font-medium ${outcome === value ? "bg-ink text-paper" : "bg-mist text-ink hover:bg-line"}`}>{label}</button>
                ))}
              </div>
            </DrawerField>
            <DrawerField label="Duration in minutes" htmlFor="log-minutes" optional error={minutesValid ? "" : "Enter minutes as a number, up to 600."}>
              <input id="log-minutes" inputMode="decimal" value={minutes} onChange={(e) => setMinutes(e.target.value.replace(/[^\d.]/g, ""))} placeholder="e.g. 4" aria-invalid={!minutesValid} className={`${drawerInput} max-w-40`} />
            </DrawerField>
          </DrawerGroup>
        )}
        {type === "Meeting" && (
          <DrawerGroup title="Meeting">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <DrawerField label="Date" htmlFor="log-meet-date"><input id="log-meet-date" type="date" value={meetDate} onChange={(e) => setMeetDate(e.target.value)} className={drawerInput} /></DrawerField>
              <DrawerField label="Time" htmlFor="log-meet-time"><TimeInput id="log-meet-time" value={meetTime} onChange={setMeetTime} ariaLabel="Meeting time" className="w-full" /></DrawerField>
            </div>
            <DrawerField label="Where" htmlFor="log-meet-location" optional><input id="log-meet-location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Clinic, video call…" className={drawerInput} /></DrawerField>
            <DrawerField label="Outcome" htmlFor="log-meet-outcome" optional><input id="log-meet-outcome" value={meetOutcome} onChange={(e) => setMeetOutcome(e.target.value)} placeholder="e.g. Agreed to book a consultation" className={drawerInput} /></DrawerField>
          </DrawerGroup>
        )}
        <DrawerGroup>
          <DrawerField label={type === "WhatsApp" || type === "Email" ? "What was said" : "Notes"} htmlFor="log-notes" optional={type === "Call" || type === "Meeting"}>
            <textarea id="log-notes" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={type === "WhatsApp" ? "Sent the price list and timings" : type === "Email" ? "Emailed the treatment estimate" : "Anything the team should know"} className="w-full resize-y rounded-sm border border-line bg-paper px-3.5 py-3 text-small text-ink outline-none placeholder:text-muted/70 focus:border-ink focus:ring-2 focus:ring-ink/10" />
          </DrawerField>
        </DrawerGroup>
        {error && <p role="alert" className="mt-4 rounded-sm bg-danger-soft px-3 py-2 text-small text-danger">{error}</p>}
      </div>
    </Drawer>
  );
}
